import { useState } from "react";
import { trpc } from "../../trpc";
import { StepMerchant } from "./wizard/StepMerchant";
import { StepProject } from "./wizard/StepProject";
import { StepEmployee } from "./wizard/StepEmployee";
import { StepAgent } from "./wizard/StepAgent";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Stepper, useSteps } from "@/components/ui/stepper";
import { toast } from "sonner";

type WizardData = {
  merchant: {
    id: string;
    displayName: string;
    whatsappAccessToken: string;
    whatsappPhoneNumberId: string;
    billingCycleDay?: number;
    minimumSubscriptionUsage?: number;
    unitPriceBelowMinimum?: number;
    unitPriceAboveMinimum?: number;
  } | null;
  project: {
    id: string;
    merchantId: string;
    code: string;
    name: string;
    calId?: string;
    calLink?: string;
    appointmentsHours?: {
      weekDayStart?: string;
      weekDayEnd?: string;
      visitOnSaturdayAllowed?: boolean;
      visitOnSundayAllowed?: boolean;
      saturdayDayStart?: string;
      saturdayDayEnd?: string;
      sundayDayStart?: string;
      sundayDayEnd?: string;
      minimumAdvanceHours?: number;
    };
  } | null;
  agent: {
    tools: ("transferConversation" | "discardLead" | "scheduleVisit" | "assignAgent")[];
    systemPrompt: string;
  } | null;
  employee: {
    name: string;
    phone: string;
    email?: string;
    status: "active" | "inactive";
  } | null;
};

type AgentWizardProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

const steps = [
  { title: "Merchant", description: "Seleccionar o crear merchant" },
  { title: "Proyecto", description: "Seleccionar o crear proyecto" },
  { title: "Agente", description: "Configurar herramientas y prompt" },
  { title: "Empleado", description: "Crear empleado" },
];

export const AgentWizard = ({ isOpen, onClose, onSuccess }: AgentWizardProps) => {
  const { activeStep, goToNext, goToPrevious } = useSteps({ count: steps.length });
  const [wizardData, setWizardData] = useState<WizardData>({
    merchant: null,
    project: null,
    agent: null,
    employee: null,
  });

  const createMerchantMutation = trpc.agent.createMerchant.useMutation();
  const createProjectMutation = trpc.agent.createProject.useMutation();
  const createAgentMutation = trpc.agent.createAgent.useMutation();
  const createEmployeeMutation = trpc.agent.createEmployee.useMutation();

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      goToNext();
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      goToPrevious();
    }
  };

  const handleReset = () => {
    goToPrevious();
    goToPrevious();
    goToPrevious();
    goToPrevious();
    setWizardData({
      merchant: null,
      project: null,
      agent: null,
      employee: null,
    });
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const updateWizardData = <K extends keyof WizardData>(step: K, data: WizardData[K]) => {
    setWizardData((prev) => ({
      ...prev,
      [step]: data,
    }));
  };

  const handleMerchantSubmit = async (merchantData: any, createNew: boolean) => {
    try {
      if (createNew) {
        const result = await createMerchantMutation.mutateAsync(merchantData);
        updateWizardData("merchant", {
          id: result.id,
          displayName: merchantData.displayName,
          whatsappAccessToken: merchantData.whatsappAccessToken,
          whatsappPhoneNumberId: merchantData.whatsappPhoneNumberId,
          billingCycleDay: merchantData.billingCycleDay,
          minimumSubscriptionUsage: merchantData.minimumSubscriptionUsage,
          unitPriceBelowMinimum: merchantData.unitPriceBelowMinimum,
          unitPriceAboveMinimum: merchantData.unitPriceAboveMinimum,
        });
      } else {
        updateWizardData("merchant", merchantData);
      }
      handleNext();
    } catch (error: any) {
      toast.error(error.message || "Error creating merchant");
    }
  };

  const handleProjectSubmit = async (projectData: any, createNew: boolean) => {
    try {
      if (createNew && wizardData.merchant) {
        const result = await createProjectMutation.mutateAsync({
          ...projectData,
          merchantId: wizardData.merchant.id,
        });
        updateWizardData("project", result);
      } else {
        updateWizardData("project", projectData);
      }
      handleNext();
    } catch (error: any) {
      toast.error(error.message || "Error creating project");
    }
  };

  const handleAgentSubmit = async (agentData: any) => {
    updateWizardData("agent", agentData);
    handleNext();
  };

  const handleEmployeeSubmit = async (employeeData: any) => {
    try {
      if (!wizardData.merchant || !wizardData.project || !wizardData.agent) {
        throw new Error("Missing required data");
      }

      await createAgentMutation.mutateAsync({
        merchantId: wizardData.merchant.id,
        projectId: wizardData.project.id,
        tools: wizardData.agent.tools,
        systemPrompt: wizardData.agent.systemPrompt,
      });

      await createEmployeeMutation.mutateAsync({
        merchantId: wizardData.merchant.id,
        projectId: wizardData.project.id,
        ...employeeData,
      });

      toast.success("Agente y empleados creados exitosamente");

      handleClose();
      onSuccess?.();
    } catch (error: any) {
      toast.error(error.message || "Error creating agent and employee");
    }
  };

  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return (
          <StepMerchant
            onSubmit={handleMerchantSubmit}
            initialData={wizardData.merchant}
            isLoading={createMerchantMutation.isLoading}
          />
        );
      case 1:
        return (
          <StepProject
            merchantId={wizardData.merchant?.id || ""}
            onSubmit={handleProjectSubmit}
            initialData={wizardData.project}
            isLoading={createProjectMutation.isLoading}
          />
        );
      case 2:
        return <StepAgent onSubmit={handleAgentSubmit} initialData={wizardData.agent} />;
      case 3:
        return (
          <StepEmployee
            onSubmit={handleEmployeeSubmit}
            initialData={wizardData.employee}
            isLoading={createAgentMutation.isLoading || createEmployeeMutation.isLoading}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-6xl">
        <DialogHeader>
          <DialogTitle>Crear Nuevo Agente</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-8">
          <Stepper activeStep={activeStep} steps={steps.map((s) => ({ title: s.title }))} />

          <div className="min-h-[400px]">{renderStep()}</div>

          <div className="flex justify-between">
            <Button variant="ghost" onClick={handleBack} disabled={activeStep === 0}>
              Atrás
            </Button>
            <Button variant="ghost" onClick={handleClose}>
              Cancelar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
